import { maxOne } from 'rise-ui/utils/array.js';
import { getCanvasTextSize } from 'rise-ui/utils/canvas.js';
import { call, identity } from 'rise-ui/utils/function.js';
import { RADIANS_PER_DEGREE } from 'rise-ui/utils/math.js';
import { alignForLine, alignForText } from 'rise-ui/utils/canvas.js';
import { parseDrawOptions } from 'rise-ui/utils/canvas/graph-draw.js';

const ROTATE_90 = Math.PI / 2;

function center(scale) {
  let offset = Math.max(0, scale.bandwidth() - 1) / 2; // Adjust for 0.5px offset.
  if (scale.round()) {
    offset = Math.round(offset);
  }
  return function(d) {
    return +scale(d) + offset;
  };
}

function number(scale) {
  return function(d) {
    return +scale(d);
  };
}

const slice = Array.prototype.slice;

function parseGraphAxisOptions(options) {
  options = parseDrawOptions(options);
  Object.assign(options, {
    tickValues: options.tickValues ? slice.call(options.tickValues) : [],
    tickSizeInner: options.tickSizeInner || options.tickSize || 6,
    tickSizeOuter: options.tickSizeOuter || options.tickSize || 6,
    tickPadding: options.tickPadding || 3,
    // Note: Leave a little extra padding for x-axis, as y-axis padding may
    // appear extended by the lack of hanging characters of tick text heights.
    labelPaddingX: options.labelPaddingX || options.labelPadding || 8,
    labelPaddingY: options.labelPaddingY || options.labelPadding || 4,
  });
  return options;
}

/**
 * @name DrawGraphAxisScaleArg
 * @function
 * @property {function} copy
 * @property {function} domain
 * @property {function} range
 * @property {function} [bandwidth]
 * @property {function} [ticks]
 * @property {function} [tickFormat]
 */
/**
 * @name DrawGraphAxisOptionsArg
 * @extends DrawOptions
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [strokeStyle] (default: '#000')
 * @property {'top'|'right'|'bottom'|'left'} [orient] (default: 'bottom')
 * @property {[]} [tickArguments]
 * @property {function} [tickFormat]
 * @property {number} [tickPadding] (default: 3)
 * @property {number} [labelPadding]
 * @property {number} [labelPaddingX] (default: 8)
 * @property {number} [labelPaddingY] (default: 4)
 * @property {number} [tickSize] Set tickSizeInner and tickSizeOuter at once
 * @property {number} [tickSizeInner] (default: 6)
 * @property {number} [tickSizeOuter] (default: 6)
 * @property {number} [tickAngle] Angle of tick labels
 * @property {'start'|'center'|'end'} [tickAlign]
 * @property {[]} [tickValues]
 */
/**
 * Draw graph axis
 * @param {CanvasRenderingContext2D} context
 * @param {DrawGraphAxisScaleArg} scale
 * @param {DrawGraphAxisOptionsArg} options
 */
export function drawGraphAxis(context, scale, options) {
  let { width, height } = options;
  const noWidth = !width && width !== 0;
  const noHeight = !height && height !== 0;
  if (noWidth || noHeight) {
    const size = getGraphAxisSize(scale, { context, ...options, width: null, height: null });
    if (noWidth) {
      width = size.width;
    }
    if (noHeight) {
      height = size.height;
    }
  }
  if (width === 0 || height === 0) {
    return;
  }

  options = parseGraphAxisOptions(options);
  const {
    paddingLeft, paddingTop,
    tickArguments, tickValues, tickFormat, tickAlign, tickAngle = 0,
    tickSizeOuter,
    fontMultiplier, fontFamily, lineHeight,
    strokeStyle, fontColor,
  } = options;

  const [ top, right, bottom, left ] = [ 1, 2, 3, 4 ];
  const orient = { top, right, bottom, left }[options.orient] || bottom;
  const isNegative = orient === top || orient === left;
  const isVertical = orient === left || orient === right;
  const k = isNegative ? -1 : 1;
  const x = isVertical ? 'x' : 'y';
  const y = isVertical ? 'y' : 'x';
  const w = isVertical ? 'width' : 'height';

  const translate = v => ({ x: v.x + 0.5, y: v.y + 0.5 });
  const axisOrigin = {
    x: paddingLeft,
    y: paddingTop,
  };
  axisOrigin[x] += orient === top ? height - 1 : orient === left ? width - 1 : 0;

  const tickArgs = [].concat(
    call(tickArguments, tickArguments, isVertical ? height : width, scale) || []
  );
  let values = tickValues
    || scale.ticks ? scale.ticks.call(scale, ...tickArgs) : scale.domain();
  const format = tickFormat
    || scale.tickFormat && scale.tickFormat.call(scale, ...tickArgs)
    || identity;
  let range = scale.range();
  let range0 = axisOrigin[y] + range[0];
  let range1 = axisOrigin[y] + range[range.length - 1];
  let position = (scale.bandwidth ? center : number)(scale.copy());

  const fontSize = options.fontSize * fontMultiplier;
  const fontOptions = {
    font: fontFamily,
    fontSize,
    lineHeight,
  };
  const tickFontOptions = {
    ...fontOptions,
    rotate: tickAngle,
    context: options.context ?? context,
  };

  const fontOffset = fontSize * 0.35;
  context.font = `${fontSize}px ${fontFamily}`;
  context.textAlign = tickAlign
    || (tickAngle && orient === bottom ? 'end' : orient === top ? 'start' : null)
    || (orient === right ? 'start' : orient === left ? 'end' : 'center');
  context.textBaseline = context.textAlign !== 'center'
    ? 'alphabetic' : (orient === top || orient === left) ? 'bottom' : 'top';
  context.strokeStyle = strokeStyle || '#000';
  context.fillStyle = fontColor || strokeStyle || '#000';
  context.lineCap = 'round';

  context.beginPath();

  const tickPadding = k * options.tickPadding;
  const tickSizeInner = k * options.tickSizeInner;
  let tickLabelSize = tickSizeOuter || 0;
  for (let i = 0, len = values.length; i < len; ++i) {
    const d = values[i];
    const value = format(d);
    const size = getCanvasTextSize(value, tickFontOptions);
    tickLabelSize = maxOne(size[w], tickLabelSize);
    const tick = translate({ ...axisOrigin });
    tick[y] += position(d);
    context.moveTo(alignForLine(tick.x), alignForLine(tick.y));
    tick[x] += tickSizeInner;
    context.lineTo(alignForLine(tick.x), alignForLine(tick.y));
    tick[x] += tickPadding;
    if (tickAngle) {
      context.save();
      context.translate(tick.x, tick.y + fontOffset);
      context.rotate(tickAngle * RADIANS_PER_DEGREE);
      context.fillText(value, alignForText(0), alignForText(0));
      context.restore();
    } else {
      context.fillText(value, alignForText(tick.x), alignForText(tick.y + fontOffset));
    }
  }

  const outer = translate({ ...axisOrigin });
  const edge = outer[x];
  const tickOuter = outer[x] + k * tickSizeOuter;
  outer[x] = tickOuter;
  outer[y] = range0 + 0.5;
  context.moveTo(alignForLine(outer.x), alignForLine(outer.y));
  outer[x] = edge;
  context.lineTo(alignForLine(outer.x), alignForLine(outer.y));
  outer[y] = range1 + 0.5;
  context.lineTo(alignForLine(outer.x), alignForLine(outer.y));
  outer[x] = tickOuter;
  context.lineTo(alignForLine(outer.x), alignForLine(outer.y));

  const { label } = options;
  if (label) {
    const labelPadding = options[isVertical ? 'labelPaddingX' : 'labelPaddingY'];
    const pos = translate({ ...axisOrigin });
    const labelPos = options.tickSizeInner + options.tickPadding
      + tickLabelSize + labelPadding;
    pos[x] += k * labelPos;
    pos[y] = (range1 - range0) / 2 + range0;
    const fontOff = k * fontOffset;
    if (isVertical) {
      const { flipXAxisLabel } = options;
      const isLeft = orient === 'left';
      const isFlipped = isLeft !== flipXAxisLabel;
      context.save();
      context.textBaseline = isFlipped ? 'ideographic' : 'top';
      context.translate(pos.x, pos.y + fontOff);
      context.rotate(isFlipped ? -ROTATE_90 : ROTATE_90);
      context.textAlign = 'center';
      context.fillText(label, alignForText(0), alignForText(0));
      context.restore();
    } else {
      context.textBaseline = 'top';
      context.fillText(label, alignForText(pos.x), alignForText(pos.y));
    }
  }

  context.stroke();
}

export function getGraphAxisSize(scale, options) {
  let width = options.width;
  let height = options.height;
  if (width === 0 || height === 0) {
    return { width: 0, height: 0 };
  }

  options = parseGraphAxisOptions(options);

  const tickArguments = options.tickArguments;
  const values = options.tickValues
    || scale.ticks ? scale.ticks.apply(scale, tickArguments) : scale.domain();
  const format = options.tickFormat
    || scale.tickFormat && scale.tickFormat.apply(scale, tickArguments)
    || identity;

  const [ top, right, bottom, left ] = [ 1, 2, 3, 4 ];
  const orient = { top, right, bottom, left }[options.orient] || bottom;
  const isVertical = orient === left || orient === right;
  const w = isVertical ? 'width' : 'height';
  const h = isVertical ? 'height' : 'width';

  const range = scale.range();
  const range0 = +range[0];
  const range1 = +range[range.length - 1];

  const fontSize = options.fontSize * options.fontMultiplier;
  const fontOptions = {
    font: options.fontFamily,
    fontSize,
    lineHeight: options.lineHeight,
    context: options.context,
  };
  const tickFontOptions = {
    ...fontOptions,
    rotate: options.tickAngle,
  };

  let tickLabelSize = options.tickSizeOuter || 0;
  for (let i = 0, len = values.length; i < len; ++i) {
    const d = values[i];
    tickLabelSize = maxOne(getCanvasTextSize(format(d), tickFontOptions)[w], tickLabelSize);
  }

  const { label } = options;
  const labelPadding = options[isVertical ? 'labelPaddingX' : 'labelPaddingY'];
  let labelSize = label ? labelPadding + getCanvasTextSize(label, fontOptions).height : 0;

  const size = {
    width: options.paddingLeft,
    height: options.paddingTop,
  };
  size[w] = Math.ceil(options[w] || size[w] + options.tickSizeInner
    + options.tickPadding + tickLabelSize + labelSize + 1);
  size[h] = Math.ceil(options[h] || size[h] + Math.abs(range1 - range0) + 1);
  return size;
}
